import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';
import { BlocManagementPricing, PricingViewState } from '../../../bloc/BlocManagementPricing';
import { Pricing } from '../../../../types/cms.types';
import { CpnPricingForm } from './components/CpnPricingForm';
import { CpnCopyPricingDialog } from './components/CpnCopyPricingDialog';
import { VIETNAM_PROVINCES, getProvinceOptions } from '../../../../utils/provinces';
import './MotorbikePricingPage.css';

export const MotorbikePricingPage: React.FC = () => {
    // Bloc instance
    const [bloc] = useState(() => new BlocManagementPricing());

    // UI state
    const [selectedProvince, setSelectedProvince] = useState<string>('');

    // Form dialog state
    const [showFormDialog, setShowFormDialog] = useState<boolean>(false);
    const [selectedPricing, setSelectedPricing] = useState<Pricing | null>(null);

    // Copy dialog state
    const [showCopyDialog, setShowCopyDialog] = useState<boolean>(false);
    const [pricingToCopy, setPricingToCopy] = useState<Pricing | null>(null);

    // Refs
    const toast = useRef<Toast>(null);

    // Load data on component mount
    useEffect(() => {
        // Set filter for motorbike only
        bloc.applyFilters({ vehicle_type: 'motorbike' });

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // Handle province change
    const handleProvinceChange = (province: string) => {
        setSelectedProvince(province);

        if (province) {
            // Apply local filter ngay khi chọn tỉnh thành
            bloc.searchLocalProvince(province);
        } else {
            // Clear local filter khi bỏ chọn tỉnh thành
            bloc.clearLocalFilter();
        }
    };

    // Handle create
    const handleCreate = () => {
        setSelectedPricing(null);
        setShowFormDialog(true);
    };

    // Handle edit
    const handleEdit = (pricing: Pricing) => {
        setSelectedPricing(pricing);
        setShowFormDialog(true);
    };

    // Handle delete
    const handleDelete = (pricing: Pricing) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa bảng giá của ${pricing.province}?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await bloc.deletePricing(pricing.id!);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Xóa bảng giá thành công'
                    });
                } catch (error) {
                    console.error('Error deleting pricing:', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: 'Có lỗi xảy ra khi xóa bảng giá'
                    });
                }
            }
        });
    };

    // Handle copy
    const handleCopy = (pricing: Pricing) => {
        setPricingToCopy(pricing);
        setShowCopyDialog(true);
    };

    // Handle copy pricing to multiple provinces
    const handleCopyPricing = async (targetProvinces: string[]) => {
        if (!pricingToCopy) return;

        try {
            const result = await bloc.copyPricingToProvinces(pricingToCopy.id!, targetProvinces);

            if (result.failed > 0) {
                // Có một số lỗi, hiển thị chi tiết
                const failedProvinces = result.results
                    .filter(r => !r.success)
                    .map(r => `${r.province}: ${r.error}`)
                    .join('\n');

                toast.current?.show({
                    severity: 'warn',
                    summary: 'Hoàn thành với một số lỗi',
                    detail: `Thành công: ${result.success}, Thất bại: ${result.failed}\n\nLỗi:\n${failedProvinces}`,
                    life: 8000
                });
            } else {
                // Tất cả thành công
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: `Đã sao chép bảng giá cho ${result.success} tỉnh thành`
                });
            }
        } catch (error) {
            console.error('Error copying pricing:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Có lỗi xảy ra khi sao chép bảng giá'
            });
        }
    };

    // Handle save
    const handleSave = async (pricingData: Omit<Pricing, 'id' | 'created_at' | 'updated_at'>) => {
        try {
            // Ensure vehicle_type is motorbike
            const dataToSave = {
                ...pricingData,
                vehicle_type: 'motorbike' as const,
                car_seats: null // Xe máy không có số chỗ ngồi
            };

            if (selectedPricing) {
                // Update existing
                await bloc.updatePricing(selectedPricing.id!, dataToSave);
            } else {
                // Create new
                await bloc.createPricing(dataToSave);
            }
        } catch (error) {
            console.error('Error saving pricing:', error);
            throw error;
        }
    };

    // Format currency
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            minimumFractionDigits: 0
        }).format(value);
    };

    // Format percentage
    const formatPercentage = (value: number) => {
        return `${value}%`;
    };

    // Column templates
    const provinceTemplate = (rowData: Pricing) => {
        return <span className="font-medium">{rowData.province}</span>;
    };

    const basePriceTemplate = (rowData: Pricing) => {
        return <span className="text-primary font-semibold">{formatCurrency(rowData.base_price)}</span>;
    };

    const serviceFeeTemplate = (rowData: Pricing) => {
        return <span>{formatCurrency(rowData.service_fee)}</span>;
    };

    const pricePerMinuteTemplate = (rowData: Pricing) => {
        return <span>{formatCurrency(rowData.price_per_minute)}</span>;
    };

    const feesTemplate = (rowData: Pricing) => {
        return (
            <div className="flex flex-column gap-1 text-sm">
                <span>21h: {formatPercentage(rowData.fee_after_21h)}</span>
                <span>0h: {formatPercentage(rowData.fee_after_0h)}</span>
                <span>Cao điểm: {formatPercentage(rowData.fee_peak_hours)}</span>
                <span>Mưa: {formatPercentage(rowData.fee_rainy)}</span>
            </div>
        );
    };

    const distancePricesTemplate = (rowData: Pricing) => {
        return (
            <div className="flex flex-column gap-1 text-sm">
                <span>2-10km: {formatCurrency(rowData.price_2_to_10km)}</span>
                <span>11-20km: {formatCurrency(rowData.price_11_to_20km)}</span>
                <span>Trên 20km: {formatCurrency(rowData.price_over_20km)}</span>
                <span>Trên 50km: {formatCurrency(rowData.price_over_50km)}</span>
                <span>Trên 100km: {formatCurrency(rowData.price_over_100km)}</span>
            </div>
        );
    };

    const actionTemplate = (rowData: Pricing) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-copy"
                    className="p-button-text p-button-sm p-button-info"
                    onClick={() => handleCopy(rowData)}
                    tooltip="Sao chép sang tỉnh khác"
                />
                <Button
                    icon="pi pi-pencil"
                    className="p-button-text p-button-sm"
                    onClick={() => handleEdit(rowData)}
                    tooltip="Chỉnh sửa"
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() => handleDelete(rowData)}
                    tooltip="Xóa"
                />
            </div>
        );
    };

    const createdAtTemplate = (rowData: Pricing) => {
        if (!rowData.created_at) return '';
        return new Date(rowData.created_at).toLocaleDateString('vi-VN');
    };

    return (
        <AdminProtectedRoute
            requiredModule="pricing_management"
            requiredSubModule="pricing_motobike"
            requiredPermission="view"
        >
            <AdminLayout>
                <div className="">
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    <StreamBuilder
                        stream={bloc.getStream("info")}
                        initialData={bloc.info}
                        builder={(snapshot) => {
                            const state = snapshot.data as PricingViewState;
                            if (!state) return <div>Loading...</div>;
                            return (
                                <div className="">
                                    {/* Data Table */}
                                    <div className="">
                                        <div className="">
                                            <div className="p-card-body">
                                                {state.error && (
                                                    <div className="error-message p-3 mb-3 bg-red-50 border border-red-200 text-red-700 rounded">
                                                        <i className="pi pi-exclamation-triangle mr-2" />
                                                        {state.error}
                                                    </div>
                                                )}

                                                <DataTable
                                                    scrollHeight='calc(100vh - 200px)'

                                                    value={state.pricings}
                                                    loading={state.loading}
                                                    emptyMessage="Không có dữ liệu"
                                                    responsiveLayout="scroll"

                                                    className="trips-management-table"
                                                    stripedRows
                                                    showGridlines
                                                    header={

                                                        <div className="">
                                                            <div className="flex justify-content-between align-items-center p-3">
                                                                <div className="flex align-items-center gap-3 flex-1">
                                                                    <h3 className="m-0">Danh sách bảng giá xe máy</h3>
                                                                    <span className="text-500">({state.pricings.length} tỉnh thành)</span>
                                                                </div>
                                                                <div className="flex align-items-center gap-3" style={{ flexShrink: 0, width: 'auto' }}>
                                                                    <div style={{ width: '250px' }}>
                                                                        <Dropdown
                                                                            value={selectedProvince}
                                                                            options={getProvinceOptions()}
                                                                            onChange={(e) => handleProvinceChange(e.value)}
                                                                            placeholder="Lọc theo tỉnh thành"
                                                                            className="w-full"
                                                                            filter
                                                                            showClear
                                                                        />
                                                                    </div>
                                                                    <Button
                                                                        label="Tạo mới"
                                                                        icon="pi pi-plus"
                                                                        onClick={handleCreate}
                                                                        className="p-button-primary"
                                                                        style={{ whiteSpace: 'nowrap' }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                    }
                                                >
                                                    <Column
                                                        field="province"
                                                        header="Tỉnh thành"
                                                        body={provinceTemplate}
                                                        sortable
                                                        style={{ minWidth: '150px' }}
                                                    />
                                                    <Column
                                                        field="base_price"
                                                        header="Giá cơ bản"
                                                        body={basePriceTemplate}
                                                        sortable
                                                        style={{ minWidth: '120px' }}
                                                    />
                                                    <Column
                                                        header="Giá theo khoảng cách"
                                                        body={distancePricesTemplate}
                                                        style={{ minWidth: '200px' }}
                                                    />
                                                    <Column
                                                        field="service_fee"
                                                        header="Phí dịch vụ"
                                                        body={serviceFeeTemplate}
                                                        sortable
                                                        style={{ minWidth: '120px' }}
                                                    />
                                                    <Column
                                                        field="price_per_minute"
                                                        header="Giá/phút"
                                                        body={pricePerMinuteTemplate}
                                                        sortable
                                                        style={{ minWidth: '120px' }}
                                                    />
                                                    <Column
                                                        header="Phí phụ thu"
                                                        body={feesTemplate}
                                                        style={{ minWidth: '150px' }}
                                                    />
                                                    <Column
                                                        field="created_at"
                                                        header="Ngày tạo"
                                                        body={createdAtTemplate}
                                                        sortable
                                                        style={{ minWidth: '120px' }}
                                                    />
                                                    <Column
                                                        header="Thao tác"
                                                        body={actionTemplate}
                                                        style={{ minWidth: '120px' }}
                                                    />
                                                </DataTable>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }}
                    />

                    {/* Form Dialog */}
                    <CpnPricingForm
                        visible={showFormDialog}
                        onHide={() => setShowFormDialog(false)}
                        pricing={selectedPricing}
                        onSave={handleSave}
                        provinces={VIETNAM_PROVINCES}
                        fixedVehicleType="motorbike"
                        fixedCarSeats={null}
                    />

                    {/* Copy Dialog */}
                    <CpnCopyPricingDialog
                        visible={showCopyDialog}
                        onHide={() => setShowCopyDialog(false)}
                        sourcePricing={pricingToCopy}
                        onCopy={handleCopyPricing}
                    />
                </div>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};
